/**
 * What this package actually requires — the derived ProjectDemandProfile.
 *
 * Every field carries where it came from. Three honesty rules the backend
 * contract forces and this panel keeps:
 *
 *  1. A missing fact is the string "UNKNOWN", not an empty value. It renders
 *     as UNKNOWN, never as a blank that reads like "nothing required".
 *  2. `insuranceRequired` / `bonding.required` are never `false` — they are
 *     `true` or UNKNOWN. "We have no record" must never be shown as "not
 *     required".
 *  3. A CONFLICT (two sources disagree) picks no winner here either.
 */
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { UnknownValue } from "../../components/workflow/WorkflowStates";
import {
  readField,
  readRequirementFlag,
  provenanceLabel,
  formatDay,
} from "./exchangePresentation";
import {
  MapPin,
  CalendarClock,
  Boxes,
  FileText,
  ShieldCheck,
  KeyRound,
  Timer,
  Wallet,
  Hammer,
  AlertTriangle,
  Flame,
} from "lucide-react";

const PROVENANCE_TONE = {
  evidenced: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  record: "bg-blue-500/15 text-blue-400 border-blue-500/25",
  derived: "bg-violet-500/15 text-violet-400 border-violet-500/25",
  operator: "bg-amber-500/15 text-amber-400 border-amber-500/25",
  assumption: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  conflict: "bg-red-500/15 text-red-400 border-red-500/25",
  unknown: "bg-muted text-muted-foreground border-border",
};

export const ProvenanceBadge = ({ provenance }) => {
  const spec = provenanceLabel(provenance);
  if (!spec) return null;
  return (
    <Badge
      variant="outline"
      className={`${PROVENANCE_TONE[spec.tone] || PROVENANCE_TONE.unknown} font-normal text-[10px]`}
      title={`Provenance: ${provenance}`}
    >
      {spec.label}
    </Badge>
  );
};

/** One labelled fact with its provenance, honest about UNKNOWN and CONFLICT. */
export const DemandField = ({ label, field, format, hint }) => {
  const read = readField(field);
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      {read.state === "CONFLICT" ? (
        <div className="space-y-0.5">
          <span className="text-sm font-medium text-red-400 inline-flex items-center gap-1">
            <Flame className="w-3.5 h-3.5" />
            Sources disagree
          </span>
          {read.candidates.map((candidate, i) => (
            <p key={i} className="text-[11px] text-muted-foreground">
              {String(candidate?.value)}
              {candidate?.source?.recordType ? ` — ${candidate.source.recordType}` : ""}
            </p>
          ))}
        </div>
      ) : read.state === "UNKNOWN" ? (
        <UnknownValue reason={hint || "No source states this."} />
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium">
            {format ? format(read.value) : String(read.value)}
          </span>
          <ProvenanceBadge provenance={read.provenance} />
        </div>
      )}
    </div>
  );
};

const Section = ({ icon: Icon, title, children, basis }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-1.5">
      <Icon className="w-4 h-4 text-primary" />
      <h4 className="text-sm font-semibold">{title}</h4>
    </div>
    {children}
    {basis && <p className="text-[10px] text-muted-foreground">{basis}</p>}
  </div>
);

const RequirementList = ({ rows, emptyLabel }) => {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="space-y-1">
      {rows.map((row, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5 rounded bg-muted/30 px-2 py-1">
          <span className="text-xs font-medium">{row.value || row.title || "—"}</span>
          {row.title && row.value !== row.title && (
            <span className="text-[11px] text-muted-foreground">{row.title}</span>
          )}
          {row.isHardGate && (
            <Badge
              variant="outline"
              className="bg-red-500/15 text-red-400 border-red-500/25 font-normal text-[10px]"
            >
              Hard gate
            </Badge>
          )}
          <ProvenanceBadge provenance={row.provenance} />
        </div>
      ))}
    </div>
  );
};

/** Backend dates arrive as ISO dates or full timestamps; show the calendar day. */
const fmtDate = (value) => formatDay(value) || String(value);

const DemandProfilePanel = ({ profile }) => {
  if (!profile) return null;

  const csiDivision = readField(profile.csi?.division);
  const insurance = readRequirementFlag(profile.credentials?.insuranceRequired);
  const bonding = readRequirementFlag(profile.credentials?.bonding?.required);
  const location = profile.location || {};
  const leadTime = readField(profile.leadTime);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        <Section icon={Hammer} title="Trade & scope" basis={profile.trade?.basis}>
          <DemandField label="Trade" field={profile.trade} />
          <div className="pt-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Package</p>
            <p className="text-sm font-medium">{profile.package?.title || "—"}</p>
            <p className="text-[11px] text-muted-foreground">
              Status {profile.package?.status || "unknown"}
              {profile.package?.dueDate ? ` · bids due ${formatDay(profile.package.dueDate)}` : ""}
            </p>
          </div>
        </Section>

        <Section icon={FileText} title="Classification" basis={profile.csi?.basis}>
          <div className="space-y-0.5">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              CSI division
            </p>
            {csiDivision.state === "CONFLICT" ? (
              <div className="space-y-0.5">
                <span className="text-sm font-medium text-red-400 inline-flex items-center gap-1">
                  <Flame className="w-3.5 h-3.5" />
                  Linked cost codes disagree
                </span>
                {csiDivision.candidates.map((candidate, i) => (
                  <p key={i} className="text-[11px] text-muted-foreground">
                    Division {String(candidate?.value)}
                  </p>
                ))}
              </div>
            ) : csiDivision.state === "UNKNOWN" ? (
              <UnknownValue reason="No linked cost code or posting states a division." />
            ) : (
              <span className="text-sm font-medium">Division {csiDivision.value}</span>
            )}
          </div>
          <div className="pt-2 space-y-0.5">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">NAICS</p>
            <UnknownValue reason="No project-side NAICS source exists in OMNΛS today." />
          </div>
        </Section>

        <Section icon={MapPin} title="Location" basis={location.basis}>
          <DemandField label="City" field={location.city} />
          <DemandField label="State" field={location.state} />
          <DemandField label="Postal code" field={location.postalCode} />
        </Section>

        <Section icon={CalendarClock} title="Dates" basis={profile.requiredOnSite?.basis}>
          <DemandField
            label="Required on site"
            field={profile.requiredOnSite}
            format={fmtDate}
            hint="No ERS or baseline date drives this package yet."
          />
          <div className="pt-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Schedule window
            </p>
            {(() => {
              const start = readField(profile.scheduleWindow?.start);
              const finish = readField(profile.scheduleWindow?.finish);
              if (start.state !== "KNOWN" && finish.state !== "KNOWN") {
                return <UnknownValue reason="No baseline window is recorded." />;
              }
              return (
                <p className="text-sm font-medium">
                  {start.state === "KNOWN" ? start.value : "unknown start"} →{" "}
                  {finish.state === "KNOWN" ? finish.value : "unknown finish"}
                </p>
              );
            })()}
          </div>
        </Section>

        <Section icon={Timer} title="Lead time exposure" basis={profile.leadTime?.basis}>
          {leadTime.state === "KNOWN" ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xl font-bold">{leadTime.value}</span>
              <span className="text-sm text-muted-foreground">days available</span>
              <ProvenanceBadge provenance={profile.leadTime?.provenance} />
            </div>
          ) : (
            <UnknownValue reason="Lead time is back-calculated from a required-on-site date; none is known." />
          )}
          {(profile.leadTime?.statedLeadTimes || []).length > 0 && (
            <div className="pt-1 space-y-0.5">
              <p className="text-[11px] text-muted-foreground">Stated by commercial records:</p>
              {profile.leadTime.statedLeadTimes.map((row, i) => (
                <p key={i} className="text-[11px] text-muted-foreground">
                  {row.days} days <ProvenanceBadge provenance={row.provenance} />
                </p>
              ))}
            </div>
          )}
        </Section>

        <Section icon={Wallet} title="Budget ceiling" basis={profile.budgetCeiling?.basis}>
          <DemandField
            label="Ceiling"
            field={profile.budgetCeiling}
            format={(value) =>
              typeof value === "number"
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 0,
                  }).format(value)
                : String(value)
            }
            hint="No estimated value is recorded on the package."
          />
        </Section>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Section icon={Boxes} title="Quantities" basis={profile.quantities?.basis}>
          {(profile.quantities?.values || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No quantity is recorded on any linked commercial or execution record.
            </p>
          ) : (
            <div className="space-y-1">
              {profile.quantities.values.map((row, i) => (
                <div key={i} className="flex items-center gap-2 rounded bg-muted/30 px-2 py-1">
                  <span className="text-sm font-medium">
                    {row.value} {row.unit || ""}
                  </span>
                  <span className="text-[11px] text-muted-foreground truncate">{row.name}</span>
                  <ProvenanceBadge provenance={row.provenance} />
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={FileText} title="Specifications" basis={profile.specifications?.basis}>
          {(profile.specifications?.values || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No specification section is cited by a linked requirement.
            </p>
          ) : (
            <div className="space-y-1">
              {profile.specifications.values.map((row, i) => (
                <div key={i} className="rounded bg-muted/30 px-2 py-1 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium">{row.specSection || row.title}</span>
                    <ProvenanceBadge provenance={row.provenance} />
                    {row.evidenceConfidence && (
                      <Badge
                        variant="outline"
                        className="bg-muted/50 border-border font-normal text-[10px]"
                      >
                        {row.evidenceConfidence}
                      </Badge>
                    )}
                  </div>
                  {row.sourceQuote && (
                    <p className="text-[11px] text-muted-foreground italic">"{row.sourceQuote}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={ShieldCheck} title="Credentials required" basis={profile.credentials?.basis}>
          <div className="space-y-2">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Licenses</p>
              <RequirementList
                rows={profile.credentials?.licenses}
                emptyLabel="No license requirement is recorded. Whether one applies is unknown."
              />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Certifications
              </p>
              <RequirementList
                rows={profile.credentials?.certifications}
                emptyLabel="No certification requirement is recorded."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Insurance
                </p>
                {insurance.state === "REQUIRED" ? (
                  <Badge
                    variant="outline"
                    className="bg-amber-500/15 text-amber-400 border-amber-500/25 font-normal"
                  >
                    Required
                  </Badge>
                ) : (
                  <UnknownValue reason="No record states an insurance obligation. Absence is a data gap, not evidence that none applies." />
                )}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Bonding</p>
                {bonding.state === "REQUIRED" ? (
                  <div className="space-y-0.5">
                    <Badge
                      variant="outline"
                      className="bg-amber-500/15 text-amber-400 border-amber-500/25 font-normal"
                    >
                      Required
                    </Badge>
                    <p className="text-[10px] text-muted-foreground">
                      Capacity unknown — OMNΛS stores no bonding amount.
                    </p>
                  </div>
                ) : (
                  <UnknownValue reason="No record states a bonding obligation." />
                )}
              </div>
            </div>
          </div>
        </Section>

        <Section icon={KeyRound} title="Security & access" basis={profile.securityAccess?.basis}>
          {(profile.securityAccess?.values || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No badging, clearance, or access authorization requirement is recorded.
            </p>
          ) : (
            <div className="space-y-1">
              {profile.securityAccess.values.map((row, i) => (
                <div key={i} className="flex flex-wrap items-center gap-1.5 rounded bg-muted/30 px-2 py-1">
                  <span className="text-xs font-medium">
                    {String(row.type || "").replaceAll("_", " ")}
                  </span>
                  <span className="text-[11px] text-muted-foreground truncate">{row.title}</span>
                  {row.isHardGate && (
                    <Badge
                      variant="outline"
                      className="bg-red-500/15 text-red-400 border-red-500/25 font-normal text-[10px]"
                    >
                      Hard gate
                    </Badge>
                  )}
                  {row.requiredDate && (
                    <span className="text-[10px] text-muted-foreground">by {row.requiredDate}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {(profile.missingInformation || []).length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-3 px-4 space-y-0.5">
            <p className="text-xs font-semibold text-amber-400 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              What OMNΛS could not establish about this scope
            </p>
            {profile.missingInformation.map((line, i) => (
              <p key={i} className="text-xs text-muted-foreground">
                {line}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {(profile.truncation?.families || []).length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Some source families exceeded the {profile.truncation.rowCap}-row scan bound (
          {profile.truncation.families.join(", ")}) — this profile reads a bounded sample of
          them.
        </p>
      )}
    </div>
  );
};

export default DemandProfilePanel;
